import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Rocket, Undo2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { api, type Job, type Shot } from '../api.js';
import { runAction } from '../actions.js';
import { ShotGrid, type ShotGroup } from './job/ShotViewer.js';
import { Screen, Section, Badge, Btn, Sheet, EmptyState, Sha, Spinner } from '../ui/kit.js';
import { reporter } from '../pwa/report.js';

// The Hold-mode moment: swipe the screenshots, read the lint verdicts and the
// diff, then one big Ship — or send it back with notes. Ship binds to the SHA
// on screen; if the branch moved, the daemon refuses and says so.
export function Review() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [shots, setShots] = useState<Shot[]>([]);
  const [shotGroups, setShotGroups] = useState<ShotGroup[]>([]);
  const [diff, setDiff] = useState<{ files: { file: string; additions: number; deletions: number }[]; total: { additions: number; deletions: number } } | null>(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.get<{ job: Job }>(`/api/jobs/${id}`).then((r) => setJob(r.job)).catch(reporter('the job'));
    void api
      .get<{ shots: Shot[]; total: number; groups: ShotGroup[] }>(`/api/jobs/${id}/shots?limit=200`)
      .then((r) => {
        setShots(r.shots);
        setShotGroups(r.groups);
      })
      .catch(reporter('the screenshots'));
    void api.get<typeof diff>(`/api/jobs/${id}/diff`).then(setDiff).catch(reporter('the review'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Prefer the CI set for the head SHA under review; fall back to self shots.
  const reviewShots = useMemo(() => {
    if (!job) return [];
    const ci = shots.filter((s) => s.source === 'ci' && (!job.headSha || s.head_sha === job.headSha));
    const base = ci.length ? ci : shots.filter((s) => s.source === 'self');
    return [...base].sort((a, b) => (a.route ?? a.file).localeCompare(b.route ?? b.file) || (a.width ?? 0) - (b.width ?? 0));
  }, [shots, job]);

  const verdictCounts = useMemo(() => {
    let fails = 0;
    let warns = 0;
    for (const s of reviewShots) {
      if (s.verdict?.verdict === 'fail') fails++;
      else if (s.verdict?.verdict === 'warn') warns++;
    }
    return { fails, warns };
  }, [reviewShots]);

  if (!job) {
    return (
      <Screen title="Review">
        <EmptyState title="Fetching…" />
      </Screen>
    );
  }

  const ship = async () => {
    setBusy(true);
    setError(null);
    const r = await runAction(id, 'ship', { headSha: job.headSha }, 'Ship');
    if (r?.ok) navigate(`/jobs/${id}`);
    else {
      setError(r?.error ?? r?.action?.error ?? 'The ship request did not go through.');
      setBusy(false);
    }
  };
  const sendBack = async () => {
    setBusy(true);
    const r = await runAction(id, 'message', { text: `Review notes from Niklas (on ${job.headSha?.slice(0, 8) ?? 'the current state'}):\n\n${notes.trim()}\n\nAddress these, run scripts/preflight.sh, and push once.` }, 'the review notes');
    if (r?.ok) navigate(`/jobs/${id}`);
    else {
      setError(r?.error ?? r?.action?.error ?? 'The notes did not go through.');
      setBusy(false);
    }
  };

  return (
    <Screen
      eyebrow="Ready to review"
      title={job.title}
      sub={
        <>
          CI green at <Sha value={job.headSha} />
          {verdictCounts.fails ? (
            <>
              {' '}
              <Badge tone="bad">{verdictCounts.fails} lint fail</Badge>
            </>
          ) : verdictCounts.warns ? (
            <>
              {' '}
              <Badge tone="accent">{verdictCounts.warns} warnings</Badge>
            </>
          ) : (
            <>
              {' '}
              <Badge tone="ok">lint clean</Badge>
            </>
          )}
        </>
      }
    >
      {reviewShots.length === 0 ? (
        <EmptyState title="No screenshots on this one.">
          A docs-only or backend-only change has nothing to show. The diff below is the review.
        </EmptyState>
      ) : (
        <>
          <ShotGrid
            jobId={job.id}
            repo={job.repo}
            shots={reviewShots}
            groups={shotGroups.filter((g) => reviewShots.some((s) => (s.route ?? '(unrouted)') === g.route))}
            total={reviewShots.length}
            testId="review-shots"
          />
          {reviewShots.some((s) => s.verdict?.findings?.length) ? (
            <div className="ops-review-findings">
              {reviewShots
                .filter((s) => s.verdict?.findings?.length)
                .map((s) => (
                  <div key={s.id} className="ops-review-findings__route">
                    <b>
                      {s.route ?? s.file}
                      {s.width ? ` · ${s.width}px` : ''}
                    </b>
                    <ul>
                      {s.verdict!.findings!.slice(0, 4).map((f, i) => (
                        <li key={i}>
                          {f.rule}: {f.element ?? ''} {f.message ? `· ${f.message}` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
            </div>
          ) : null}
        </>
      )}

      {diff && diff.files.length > 0 ? (
        <Section title="The diff" aside={`+${diff.total.additions} −${diff.total.deletions}`}>
          <div className="ops-card">
            {diff.files.slice(0, 20).map((f) => (
              <div key={f.file} className="ops-ci__row">
                <span className="ops-ci__name">{f.file}</span>
                <span style={{ color: 'var(--ops-ok)' }}>+{f.additions}</span>
                <span style={{ color: 'var(--ops-bad)' }}>−{f.deletions}</span>
              </div>
            ))}
            {diff.files.length > 20 ? (
              <div className="ops-log__entry ops-log__entry--system">…and {diff.files.length - 20} more files</div>
            ) : null}
          </div>
        </Section>
      ) : null}

      <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Btn kind="primary" big disabled={busy || !job.buttons.includes('ship')} onClick={() => void ship()}>
          {busy ? <Spinner /> : (
            <>
              <Rocket size={17} /> Ship it
            </>
          )}
        </Btn>
        <Btn kind="quiet" big disabled={busy} onClick={() => setNotesOpen(true)}>
          <Undo2 size={16} /> Send back with notes
        </Btn>
        {!job.buttons.includes('ship') ? (
          <p className="ops-hint" style={{ textAlign: 'center' }}>
            This job is {job.phase.replace(/_/g, ' ')}. Shipping is for a green PR that has not merged.
          </p>
        ) : null}
        {error ? <p className="ops-error">{error}</p> : null}
      </div>

      {notesOpen ? (
        <Sheet title="Send back with notes" onClose={() => setNotesOpen(false)}>
          <textarea
            className="ops-textarea"
            placeholder="What should change?"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <div style={{ marginTop: 14 }}>
            <Btn kind="primary" big disabled={busy || notes.trim().length < 4} onClick={() => void sendBack()}>
              Send back
            </Btn>
          </div>
        </Sheet>
      ) : null}
    </Screen>
  );
}
